import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

const CODE = "DUBAI10";

const Q = [
  {
    q: "How do you usually sleep?",
    options: [
      { v: "side", label: "On my side", emoji: "🛌" },
      { v: "back", label: "On my back", emoji: "😌" },
      { v: "stomach", label: "On my stomach", emoji: "🙃" },
      { v: "mixed", label: "I move a lot", emoji: "🔄" },
    ],
  },
  {
    q: "Do you wake up with pain or stiffness?",
    options: [
      { v: "often", label: "Yes, almost every day", emoji: "😣" },
      { v: "sometimes", label: "A few times a week", emoji: "😕" },
      { v: "rarely", label: "Rarely", emoji: "🙂" },
      { v: "never", label: "Never — just want luxury", emoji: "✨" },
    ],
  },
  {
    q: "Who is this for?",
    options: [
      { v: "1", label: "Just me", emoji: "👤", qty: 1 },
      { v: "2", label: "Me & partner", emoji: "💑", qty: 2 },
      { v: "4", label: "Whole family / gifts", emoji: "👨‍👩‍👧‍👦", qty: 4 },
    ],
  },
];

export default function SleepQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const pick = (option) => {
    const next = [...answers, option];
    setAnswers(next);
    if (step < Q.length - 1) {
      setStep(step + 1);
    } else {
      setDone(true);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers([]);
    setDone(false);
  };

  const recommendedQty = answers[2]?.qty || 2;
  const recommendedHeight =
    answers[0]?.v === "side" || answers[0]?.v === "mixed" ? "High" : "Medium";

  const copyAndShop = async () => {
    try {
      await navigator.clipboard.writeText(CODE);
      localStorage.setItem("dp_discount", CODE);
      window.dispatchEvent(new Event("dp:discount-changed"));
      setCopied(true);
      toast.success(`Code ${CODE} copied · auto-applied at checkout`);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      localStorage.setItem("dp_discount", CODE);
      window.dispatchEvent(new Event("dp:discount-changed"));
      toast.success(`Code ${CODE} saved · auto-applied at checkout`);
    }
    setTimeout(() => {
      document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" });
    }, 350);
  };

  return (
    <section className="aurora-wrap py-20 md:py-28">
      <div className="container-x max-w-3xl">
        <div className="text-center">
          <span className="section-pill">🎯 Find your match</span>
          <h2 className="mt-5 text-[32px] sm:text-[42px] md:text-[52px] leading-[1.05] font-black tracking-[-0.02em] text-[#0e0e10]">
            Your perfect pillow,
            <br />
            in 30 seconds.
          </h2>
          <p className="mt-4 text-[#5a5a60] text-[15px]">
            Answer 3 quick questions. Get your personal recommendation + 10% off.
          </p>
        </div>

        <div className="mt-10 bg-white rounded-[28px] border border-black/5 p-7 md:p-10 shadow-md min-h-[360px]">
          {!done ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8a8a8a] font-bold">
                  Step {step + 1} of {Q.length}
                </div>
                <div className="flex gap-1.5">
                  {Q.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 w-8 rounded-full ${
                        i <= step ? "bg-[#0e0e10]" : "bg-[#ececec]"
                      }`}
                    />
                  ))}
                </div>
              </div>

              <h3 className="text-[22px] md:text-[26px] font-black text-[#0e0e10] mb-6 leading-tight">
                {Q[step].q}
              </h3>

              <div className="grid sm:grid-cols-2 gap-3">
                {Q[step].options.map((o) => (
                  <button
                    key={o.v}
                    data-testid={`quiz-option-${step}-${o.v}`}
                    onClick={() => pick(o)}
                    className="text-left p-4 rounded-2xl border-2 border-[#ececec] hover:border-[#0e0e10] hover:bg-[#faf8f4] transition-all flex items-center gap-3"
                  >
                    <span className="text-2xl">{o.emoji}</span>
                    <span className="font-semibold text-[14px] md:text-[15px] text-[#0e0e10]">
                      {o.label}
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center">
              <div className="sticker sticker-lime inline-block text-[13px] mb-5">
                Your match
              </div>
              <h3 className="text-[24px] md:text-[30px] font-black text-[#0e0e10] leading-tight">
                The {recommendedHeight} loft bundle
                <br />
                of {recommendedQty} Dubai Pillow{recommendedQty > 1 ? "s" : ""}.
              </h3>
              <p className="mt-3 text-[#5a5a60] text-[14px] max-w-md mx-auto">
                Based on your answers, this combo will fix your sleep, your neck,
                and pay itself back in under a year.
              </p>

              <div className="mt-7 max-w-md mx-auto">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8a8a8a] font-bold mb-2">
                  Your 10% off code
                </div>
                <button
                  type="button"
                  onClick={copyAndShop}
                  data-testid="quiz-claim"
                  className={`w-full flex items-center justify-between gap-3 rounded-full px-5 py-4 font-extrabold transition ${
                    copied
                      ? "bg-[#22c55e] text-white"
                      : "bg-[#0e0e10] text-white hover:bg-[#d8f06b] hover:text-[#0e0e10]"
                  }`}
                >
                  <span className="text-[18px] tabular-nums tracking-[0.18em]">
                    {CODE}
                  </span>
                  <span className="text-[12px] uppercase tracking-wider inline-flex items-center gap-1.5">
                    {copied ? (
                      <>
                        <Check size={14} /> Copied · shopping
                      </>
                    ) : (
                      <>
                        <Copy size={14} /> Tap & shop
                      </>
                    )}
                  </span>
                </button>
                <p className="mt-3 text-[11px] text-[#8a8a8a]">
                  Code auto-applies at checkout · no email required
                </p>
              </div>

              <button onClick={reset} className="mt-5 text-[12px] text-[#8a8a8a] underline">
                Retake the quiz
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
